import { Alert } from 'cpool-components/general/Alert';
import { AlertState } from 'cpool-components/general/Alert/types';
import { Tabs as TabsComponent } from 'cpool-components/general/Tabs';
import type { TabItem } from 'cpool-components/general/Tabs/types';
import { CUSTOMER_DATA_TABS } from 'cpool-constants';
import { useTranslation } from 'react-i18next';

import type { WohnartValidationError } from './util/hooks/useWohnartValidation';

// Extended tabs for the CreateApplicantModal (includes EXISTING_CREDITS)
export const APPLICANT_MODAL_TABS = {
    ...CUSTOMER_DATA_TABS,
    EXISTING_CREDITS: 'EXISTING_CREDITS',
} as const;

export type ApplicantModalTab = (typeof APPLICANT_MODAL_TABS)[keyof typeof APPLICANT_MODAL_TABS];

type Props = {
    errorCounts?: Record<ApplicantModalTab, number>;
    selectedTab: ApplicantModalTab;
    onSelectTab: (tab: ApplicantModalTab) => void;
    wohnartValidationError?: WohnartValidationError;
};

export const Tabs: React.FC<Props> = ({ errorCounts, onSelectTab, selectedTab, wohnartValidationError }) => {
    const { t } = useTranslation();

    const livingTabLabel = t(`CUSTOMER_DATA_TABS.${APPLICANT_MODAL_TABS.LIVING_SITUATION}`);

    const getWohnartValidationMessage = (): string => {
        if (wohnartValidationError === 'NO_SELF_OCCUPIED_REAL_ESTATE') {
            return t('LIVING_SITUATION_DATA.NO_SELF_OCCUPIED_REAL_ESTATE_ERROR');
        }
        if (wohnartValidationError === 'LIVING_IN_OWN_REAL_ESTATE') {
            return t('LIVING_SITUATION_DATA.LIVING_IN_OWN_REAL_ESTATE_ERROR');
        }
        return '';
    };

    const tabs: TabItem[] = [
        {
            error: (errorCounts?.[APPLICANT_MODAL_TABS.PERSONAL_DATA] ?? 0) > 0,
            id: APPLICANT_MODAL_TABS.PERSONAL_DATA,
            label: t(`CUSTOMER_DATA_TABS.${APPLICANT_MODAL_TABS.PERSONAL_DATA}`),
        },
        {
            error: (errorCounts?.[APPLICANT_MODAL_TABS.INCOME_AND_EXPENSES] ?? 0) > 0,
            id: APPLICANT_MODAL_TABS.INCOME_AND_EXPENSES,
            label: t(`CUSTOMER_DATA_TABS.${APPLICANT_MODAL_TABS.INCOME_AND_EXPENSES}`),
        },
        {
            error: (errorCounts?.[APPLICANT_MODAL_TABS.LIVING_SITUATION] ?? 0) > 0,
            id: APPLICANT_MODAL_TABS.LIVING_SITUATION,
            label: livingTabLabel,
        },
        {
            error: (errorCounts?.[APPLICANT_MODAL_TABS.REAL_ESTATE] ?? 0) > 0,
            id: APPLICANT_MODAL_TABS.REAL_ESTATE,
            label: t(`CUSTOMER_DATA_TABS.${APPLICANT_MODAL_TABS.REAL_ESTATE}`),
        },
        {
            error: (errorCounts?.[APPLICANT_MODAL_TABS.EMPLOYMENT] ?? 0) > 0,
            id: APPLICANT_MODAL_TABS.EMPLOYMENT,
            label: t(`CUSTOMER_DATA_TABS.${APPLICANT_MODAL_TABS.EMPLOYMENT}`),
        },
        {
            error: (errorCounts?.[APPLICANT_MODAL_TABS.EXISTING_CREDITS] ?? 0) > 0,
            id: APPLICANT_MODAL_TABS.EXISTING_CREDITS,
            label: t(`CUSTOMER_DATA_TABS.${APPLICANT_MODAL_TABS.EXISTING_CREDITS}`),
        },
    ];

    return (
        <div className="flex flex-col gap-2">
            <TabsComponent
                tabs={tabs}
                selectedId={selectedTab}
                onSelect={(id) => onSelectTab(id as unknown as ApplicantModalTab)}
                listClassName="w-full"
            />
            {wohnartValidationError && <Alert state={AlertState.WARNING}>{getWohnartValidationMessage()}</Alert>}
        </div>
    );
};
